<template>
  <div ref="root" class="root h-full" v-bind="$attrs"></div>
  <div v-if="map.created">
    <slot></slot>
  </div>
  <ui-no-total-fire-ban-modal v-if="hazards.mapMode === MapMode.FireBan && noTotalFireBansModal.open" :modal="noTotalFireBansModal" />
</template>

<script lang="ts" setup>
import { useModal } from '@/common';
import { HazardType, MapMode, initialiseHazardSources, useHazardsStore, useMapStore } from '@/features/hazards';
import MapboxGL, { Map, MapboxOptions } from 'mapbox-gl';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

const props = defineProps<{
  options: Partial<MapboxOptions>;
}>();

const root = ref<HTMLElement>();

const map = useMapStore();
const hazards = useHazardsStore();
const options = computed<MapboxOptions>(() => {
  return { ...props.options, container: root.value! };
});

const resizeObserver = ref<ResizeObserver>();

const noTotalFireBansModal = useModal();

onMounted(() => {
  if (!props.options.accessToken) {
    console.error('No accessToken was provided to MapboxMap component');
    return;
  }

  MapboxGL.accessToken = props.options.accessToken;

  map.setMapbox(new Map(options.value));

  if (!map.mapbox) {
    return;
  }

  map.setCreated(true);

  map.mapbox.on('load', () => {
    map.setLoaded(true);
  });

  map.mapbox.on('style.load', () => {
    if (hazards.isLoadingHazards || !map.loaded) {
      return;
    }

    map.setLoaded(true);

    map.setMapModeLayer(hazards.mapMode);

    initialiseHazardSources();
  });

  watch(
    () => hazards.mapMode,
    (newMapMode) => {
      if (newMapMode === MapMode.FireDanger && !hazards.getFireDangerRatingsPolygonsGeoSource.features.length) {
        hazards.fetchAllHazards([HazardType.FireDangerRating]);
      } else if (newMapMode === MapMode.FireBan && !hazards.filteredSectionHazards.totalFireBans.filter((tfb) => tfb.isActive).length) {
        noTotalFireBansModal.setOpen();
      }
      map.setMapModeLayer(newMapMode);
    }
  );

  watch(
    () => hazards.selectedDateIndex,
    () => {
      if (hazards.mapMode === MapMode.FireBan && !hazards.filteredSectionHazards.totalFireBans.filter((tfb) => tfb.isActive).length) {
        noTotalFireBansModal.setOpen();
      }
    }
  );

  // Mapbox has some resize issues
  // Create an observer on this object
  // Call resize on the map when we change size
  resizeObserver.value = new ResizeObserver(() => {
    map.mapbox?.resize();
  });
  resizeObserver.value.observe(options.value.container as Element);

  // Move the scale control to the right when the pan controls are expanded
  watch(
    () => map.panControlsExpanded,
    () => {
      const scale = document.querySelector('.mapboxgl-ctrl-scale');
      if (!scale) return;
      (scale as HTMLElement).style.right = map.panControlsExpanded ? '200px' : '100px';
    }
  );
});

onUnmounted(() => {
  if (!map.mapbox || !resizeObserver.value) {
    return;
  }

  resizeObserver.value.disconnect();
  map.teardown();
});
</script>

<style>
.mapboxgl-popup {
  z-index: 999;
  max-width: unset !important;
}

.mapboxgl-popup-content {
  border-radius: 8px !important;
  padding: 0px !important;
}

.mapboxgl-popup-close-button {
  display: none;
}

.mapboxgl-compact {
  height: 24px;
  margin: 8px !important;
}

.mapboxgl-ctrl-scale {
  position: absolute;
  right: 100px;
  bottom: 32px;
  margin: 0 !important;
}

.mapboxgl-ctrl-bottom-left .mapboxgl-ctrl {
  margin: 8px !important;
}
</style>
